import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  Avatar,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const quickResponses = [
  "How do I analyze a property?",
  "What data sources do you use?",
  "How accurate are the estimates?",
  "Can I save my analysis?",
  "What's included in the free plan?",
];

const AIChatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hi! I'm the QuickBuildr.ai assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const generateBotResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('analyze') || lowerMessage.includes('property')) {
      return "To analyze a property, simply enter the address in our Property Analyzer tool. We'll fetch real-time data from ArcGIS and provide comprehensive development scenarios including renovation, extension, and teardown options with financial projections.";
    }
    
    if (lowerMessage.includes('data') || lowerMessage.includes('source')) {
      return "We use authenticated ArcGIS Enterprise APIs, MassGIS services, Cambridge Open Data, and municipal databases to provide real-time property information including lot dimensions, zoning, assessments, and building details.";
    }
    
    if (lowerMessage.includes('accurate') || lowerMessage.includes('reliability')) {
      return "Our data comes directly from official government sources and is updated in real-time. We achieve 98% accuracy by using authenticated ArcGIS APIs and cross-referencing multiple data sources.";
    }
    
    if (lowerMessage.includes('save') || lowerMessage.includes('export')) {
      return "Yes! You can save all your property analyses to your dashboard. Premium users can also export detailed PDF reports and Excel spreadsheets with financial projections.";
    }
    
    if (lowerMessage.includes('free') || lowerMessage.includes('pricing') || lowerMessage.includes('plan')) {
      return "Our free plan includes 5 property analyses per month with basic features. Premium plans start at $49/month for unlimited analyses, advanced features, and priority support. For sales inquiries, call 781-528-3339.";
    }
    
    if (lowerMessage.includes('contact') || lowerMessage.includes('support') || lowerMessage.includes('help')) {
      return "You can reach our support team at support@quickbuildr.ai or call us at 781-528-3339. We're available Monday-Friday, 9 AM - 6 PM EST.";
    }
    
    if (lowerMessage.includes('zoning') || lowerMessage.includes('regulation')) {
      return "We analyze properties against current Somerville and Cambridge zoning ordinances, including district requirements, setbacks, height limits, and special permits. Our system is updated with the latest 2025 zoning reforms.";
    }
    
    if (lowerMessage.includes('login') || lowerMessage.includes('sign')) {
      return "You can log in or sign up using the buttons in the top navigation bar. Creating an account is free and gives you access to save analyses and track properties.";
    }
    
    return "I'd be happy to help! You can ask me about property analysis, data sources, pricing plans, or any features of QuickBuildr.ai. For detailed support, contact us at 781-528-3339 or support@quickbuildr.ai.";
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate bot response delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: generateBotResponse(userMessage.text),
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickResponse = (question: string) => {
    setInput(question);
    handleSend();
  };

  return (
    <>
      {/* Floating Action Button */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            <Fab
              color="primary"
              onClick={handleOpen}
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #1a73e8 0%, #34a853 100%)',
                boxShadow: '0 4px 12px rgba(26, 115, 232, 0.4)',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ChatIcon />
            </Fab>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: '600px',
            maxHeight: '80vh',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1a73e8 0%, #34a853 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon />
            <Typography variant="h6">QuickBuildr.ai Assistant</Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc(100% - 64px - 80px)' }}>
          {/* Messages Area */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
            <Stack spacing={2}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  {message.sender === 'bot' && (
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <BotIcon fontSize="small" />
                    </Avatar>
                  )}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '75%',
                      bgcolor: message.sender === 'user' ? 'primary.main' : 'grey.100',
                      color: message.sender === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2">{message.text}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        opacity: 0.7,
                      }}
                    >
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Paper>
                  {message.sender === 'user' && (
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  )}
                </Box>
              ))}
              {isTyping && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    <BotIcon fontSize="small" />
                  </Avatar>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Typing...</Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Quick Responses */}
          {messages.length === 1 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick questions:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {quickResponses.map((response, index) => (
                  <Chip
                    key={index}
                    label={response}
                    onClick={() => handleQuickResponse(response)}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            size="small"
            sx={{ mr: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            endIcon={<SendIcon />}
            disabled={!input.trim() || isTyping}
            sx={{
              background: 'linear-gradient(135deg, #1a73e8 0%, #34a853 100%)',
              minWidth: 100,
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIChatbot;